export const parseReferenceString = (refString) => {
  if (typeof refString !== 'string') return null;
  
  // Match array item: @audience{audId}.actors{actorId}.{fieldName}[{idx}]
  const arrayMatch = refString.match(/@audience([^.]+)\.actors([^.]+)\.([a-zA-Z0-9_]+)\[(\d+)\]/);
  if (arrayMatch && arrayMatch.length === 5) {
    return {
      type: 'actor-array-item',
      audienceId: arrayMatch[1],
      actorId: arrayMatch[2],
      fieldName: arrayMatch[3],
      arrayIndex: parseInt(arrayMatch[4], 10)
    };
  }
  
  // Fallback to normal actor field
  const audienceMatch = refString.match(/@audience([^.]+)\.actors([^.]+)\.([^.]+)/);
  if (audienceMatch && audienceMatch.length === 4) {
    return {
      type: 'actor',
      audienceId: audienceMatch[1],
      actorId: audienceMatch[2],
      fieldName: audienceMatch[3]
    };
  }
  
  // Task array item: @task{taskId}.{fieldName}[{idx}]
  const taskArrayMatch = refString.match(/@task([^.]+)\.([a-zA-Z0-9_]+)\[(\d+)\]/);
  if (taskArrayMatch && taskArrayMatch.length === 4) {
    return {
      type: 'task-array-item',
      taskId: taskArrayMatch[1],
      fieldName: taskArrayMatch[2],
      arrayIndex: parseInt(taskArrayMatch[3], 10)
    };
  }
  
  // Fallback to normal task field
  const taskMatch = refString.match(/@task([^.]+)\.([^.]+)/);
  if (taskMatch && taskMatch.length === 3) {
    return {
      type: 'task',
      taskId: taskMatch[1],
      fieldName: taskMatch[2]
    };
  }
  return null;
};

// Note: formatReferenceForDisplay needs access to audiences, actorsByAudience, tasks data.
// It's simpler to keep a version of this within components that have this data,
// or pass the data sources to this utility function.
// For now, I'll assume we'll pass the data to a version of it.

export const formatReferenceForDisplay = (refString, audiences, actorsByAudience, tasks, studyCases) => {
  const parsedRef = parseReferenceString(refString);
  if (!parsedRef) return refString;

  if (parsedRef.type === 'actor-array-item') {
    const audience = Array.isArray(audiences) ? audiences.find(aud => aud.id === parsedRef.audienceId) : null;
    if (!audience) return refString;

    const actorsInAudience = (actorsByAudience && typeof actorsByAudience === 'object') ? actorsByAudience[parsedRef.audienceId] : null;
    if (!Array.isArray(actorsInAudience)) return refString;

    const actor = actorsInAudience.find(act => act.id === parsedRef.actorId);
    if (!actor) return refString;

    let displayText = `@${audience.name}.${actor.name}.${parsedRef.fieldName}[${parsedRef.arrayIndex}]`;
    
    // Try to get the actual value
    const arr = actor[parsedRef.fieldName];
    if (Array.isArray(arr) && arr.length > parsedRef.arrayIndex) {
      const value = arr[parsedRef.arrayIndex];
      if (typeof value === 'string') {
        displayText += `: ${value}`;
      } else if (value && typeof value === 'object' && value.text) {
        // Handle objects with text property
        displayText += `: ${value.text}`;
      } else if (value && typeof value === 'object' && value.description) {
        // Handle objects with description property
        displayText += `: ${value.description}`;
      } else if (value !== null && value !== undefined) {
        // For other objects, try to stringify safely
        try {
          displayText += `: ${JSON.stringify(value)}`;
        } catch {
          displayText += `: [Object]`;
        }
      }
    }
    
    return displayText;
  } else if (parsedRef.type === 'actor') {
    const audience = Array.isArray(audiences) ? audiences.find(aud => aud.id === parsedRef.audienceId) : null;
    if (!audience) return refString;

    const actorsInAudience = (actorsByAudience && typeof actorsByAudience === 'object') ? actorsByAudience[parsedRef.audienceId] : null;
    if (!Array.isArray(actorsInAudience)) return refString;

    const actor = actorsInAudience.find(act => act.id === parsedRef.actorId);
    if (!actor) return refString;

    return `@${audience.name}.${actor.name}.${parsedRef.fieldName}`; // Simplified display
  } else if (parsedRef.type === 'task-array-item') {
    const task = Array.isArray(tasks) ? tasks.find(t => t.id === parsedRef.taskId) : null;
    if (!task) return refString;
    
    let displayText = `@${task.name}.${parsedRef.fieldName}[${parsedRef.arrayIndex}]`;
    
    // Try to get the actual value
    const arr = task[parsedRef.fieldName];
    if (Array.isArray(arr) && arr.length > parsedRef.arrayIndex) {
      const value = arr[parsedRef.arrayIndex];
      if (typeof value === 'string') {
        displayText += `: ${value}`;
      } else if (value && typeof value === 'object' && value.text) {
        // Handle objects with text property
        displayText += `: ${value.text}`;
      } else if (value && typeof value === 'object' && value.description) {
        // Handle objects with description property
        displayText += `: ${value.description}`;
      } else if (value !== null && value !== undefined) {
        // For other objects, try to stringify safely
        try {
          displayText += `: ${JSON.stringify(value)}`;
        } catch {
          displayText += `: [Object]`;
        }
      }
    }
    
    return displayText;
  } else if (parsedRef.type === 'task') {
    const task = Array.isArray(tasks) ? tasks.find(t => t.id === parsedRef.taskId) : null;
    if (!task) return refString;
    return `@${task.name}.${parsedRef.fieldName}`; // Simplified display
  } else if (parsedRef.type === 'study-case') {
    const studyCase = Array.isArray(studyCases) ? studyCases.find(sc => sc.id === parsedRef.studyCaseId) : null;
    if (!studyCase) return refString;
    return `@${studyCase.featureName || studyCase.id}.${parsedRef.fieldName}`; // Simplified display
  }
  return refString;
};

// Helper function to get raw data for a reference
export const getRawDataForReference = (parsedRef, audiences, actorsByAudience, tasks, studyCases) => {
  if (!parsedRef) return null;

  if (parsedRef.type === 'actor-array-item') {
    const { audienceId, actorId, fieldName, arrayIndex } = parsedRef;
    const audience = Array.isArray(audiences) ? audiences.find(aud => aud.id === audienceId) : null;
    if (!audience) {
      return null;
    }
    const actors = (actorsByAudience && typeof actorsByAudience === 'object') ? actorsByAudience[audienceId] : null;
    if (!Array.isArray(actors)) {
      return null;
    }
    const actor = actors.find(act => act.id === actorId);
    if (!actor) {
      return null;
    }
    const arr = actor[fieldName];
    if (!Array.isArray(arr) || arr.length <= arrayIndex) {
      return null;
    }
    const result = arr[arrayIndex];
    return result;
  } else if (parsedRef.type === 'actor') {
    const { audienceId, actorId, fieldName } = parsedRef;
    const audience = Array.isArray(audiences) ? audiences.find(aud => aud.id === audienceId) : null;
    if (!audience) return null;
    const actors = (actorsByAudience && typeof actorsByAudience === 'object') ? actorsByAudience[audienceId] : null;
    if (!Array.isArray(actors)) return null;
    const actor = actors.find(act => act.id === actorId);
    if (!actor) return null;
    const result = (actor && typeof actor === 'object' && actor[fieldName] !== undefined) ? actor[fieldName] : null;
    return result;
  } else if (parsedRef.type === 'task-array-item') {
    const { taskId, fieldName, arrayIndex } = parsedRef;
    const task = Array.isArray(tasks) ? tasks.find(t => t.id === taskId) : null;
    if (!task) return null;
    const arr = task[fieldName];
    if (!Array.isArray(arr) || arr.length <= arrayIndex) return null;
    const result = arr[arrayIndex];
    return result;
  } else if (parsedRef.type === 'task') {
    const { taskId, fieldName } = parsedRef;
    const task = Array.isArray(tasks) ? tasks.find(t => t.id === taskId) : null;
    if (!task) return null;
    const result = (task && typeof task === 'object' && task[fieldName] !== undefined) ? task[fieldName] : null;
    return result;
  } else if (parsedRef.type === 'study-case') {
    const { studyCaseId, fieldName } = parsedRef;
    const studyCase = Array.isArray(studyCases) ? studyCases.find(sc => sc.id === studyCaseId) : null;
    if (!studyCase) return null;
    const result = (studyCase && typeof studyCase === 'object' && studyCase[fieldName] !== undefined) ? studyCase[fieldName] : null;
    return result;
  }
  return null;
};

// Utility to deeply omit createdAt/created_at from any object/array
function deepOmitCreatedAt(value) {
  if (Array.isArray(value)) {
    return value.map(deepOmitCreatedAt);
  } else if (value && typeof value === 'object') {
    const { createdAt: _createdAt, created_at: _created_at, ...rest } = value;
    const cleaned = {};
    for (const key in rest) {
      if (Object.prototype.hasOwnProperty.call(rest, key)) {
        cleaned[key] = deepOmitCreatedAt(rest[key]);
      }
    }
    return cleaned;
  }
  return value;
}

// Expression mode: replace mention chips with display names
export function processRichTextForExpression(htmlText, audiences, actorsByAudience, tasks, studyCases) {
  if (!htmlText) return '';
  const div = document.createElement('div');
  div.innerHTML = htmlText;
  div.querySelectorAll('span[data-mention-id]').forEach(span => {
    const mentionId = span.getAttribute('data-mention-id');
    if (mentionId) {
      const fullMentionId = mentionId.startsWith('@') ? mentionId : `@${mentionId}`;
      const parsedRef = parseReferenceString(fullMentionId);
      let rawData = null;
      if (parsedRef) {
        try {
          rawData = getRawDataForReference(parsedRef, audiences, actorsByAudience, tasks, studyCases);
        } catch {}
      }
      let displayText = '';
      // If it's an array of objects (Firestore subcollection), show only the description fields
      if (Array.isArray(rawData) && rawData.length > 0 && rawData.every(item => item && typeof item === 'object' && 'description' in item)) {
        displayText = rawData.map(item => item.description).filter(Boolean).join(', ');
      } else if (rawData && typeof rawData === 'object' && !Array.isArray(rawData) && 'description' in rawData) {
        displayText = rawData.description;
      } else if (rawData && typeof rawData === 'object' && !Array.isArray(rawData) && 'text' in rawData) {
        // Handle objects with text property
        displayText = rawData.text;
      } else if (rawData !== null && rawData !== undefined) {
        displayText = String(rawData);
      } else {
        displayText = formatReferenceForDisplay(fullMentionId, audiences, actorsByAudience, tasks, studyCases);
      }
      span.textContent = displayText;
      span.setAttribute('data-label', displayText);
    }
  });
  return div.innerHTML;
}

// Result mode: replace mention chips with values, omitting createdAt/created_at, using names in keys
export function processRichTextForResult(htmlText, audiences, actorsByAudience, tasks, studyCases) {
  if (!htmlText) return '';
  const div = document.createElement('div');
  div.innerHTML = htmlText;
  div.querySelectorAll('span[data-mention-id]').forEach(span => {
    const mentionId = span.getAttribute('data-mention-id');
    if (!mentionId) {
      span.replaceWith(document.createTextNode('[Invalid mention]'));
      return;
    }
    const fullMentionId = mentionId.startsWith('@') ? mentionId : `@${mentionId}`;
    const parsedRef = parseReferenceString(fullMentionId);
    if (!parsedRef) {
      span.replaceWith(document.createTextNode(`[Cannot parse: ${fullMentionId}]`));
      return;
    }
    let rawData = null;
    try {
      rawData = getRawDataForReference(parsedRef, audiences, actorsByAudience, tasks, studyCases);
    } catch (err) {
      console.error('Error getting raw data:', err);
    }
    let resolved = '';
    if (rawData !== null && rawData !== undefined) {
      const cleaned = deepOmitCreatedAt(rawData);
      if (typeof cleaned === 'object') {
        resolved = JSON.stringify(cleaned, null, 2);
      } else {
        resolved = String(cleaned);
      }
    } else {
      const displayRef = formatReferenceForDisplay(fullMentionId, audiences, actorsByAudience, tasks, studyCases);
      resolved = `[Not found: ${displayRef}]`;
    }
    span.replaceWith(document.createTextNode(resolved));
  });
  return div.textContent || '';
}

// Structured JSON result mode (for previews)
export function referencesToStructuredJson(htmlText, audiences, actorsByAudience, tasks, studyCases) {
  if (!htmlText) return '';
  const div = document.createElement('div');
  div.innerHTML = htmlText;
  const result = [];
  div.querySelectorAll('span[data-mention-id]').forEach(span => {
    const mentionId = span.getAttribute('data-mention-id');
    if (mentionId) {
      const fullMentionId = mentionId.startsWith('@') ? mentionId : `@${mentionId}`;
      const parsedRef = parseReferenceString(fullMentionId);
      if (parsedRef) {
        let value = getRawDataForReference(parsedRef, audiences, actorsByAudience, tasks, studyCases);
        value = deepOmitCreatedAt(value);
        let displayKey = fullMentionId;
        if (parsedRef.type === 'actor' || parsedRef.type === 'actor-array-item') {
          const audience = audiences.find(aud => aud.id === parsedRef.audienceId);
          const actors = actorsByAudience[parsedRef.audienceId] || [];
          const actor = actors.find(act => act.id === parsedRef.actorId);
          if (audience && actor) {
            if (parsedRef.type === 'actor-array-item') {
              displayKey = `@audience[${audience.name}].actors[${actor.name}].${parsedRef.fieldName}[${parsedRef.arrayIndex}]`;
            } else {
              displayKey = `@audience[${audience.name}].actors[${actor.name}].${parsedRef.fieldName}`;
            }
          }
        } else if (parsedRef.type === 'task' || parsedRef.type === 'task-array-item') {
          const task = tasks.find(t => t.id === parsedRef.taskId);
          if (task) {
            if (parsedRef.type === 'task-array-item') {
              displayKey = `@task[${task.name}].${parsedRef.fieldName}[${parsedRef.arrayIndex}]`;
            } else {
              displayKey = `@task[${task.name}].${parsedRef.fieldName}`;
            }
          }
        }
        result.push({ [displayKey]: value });
      }
    }
  });
  return JSON.stringify(result, null, 2);
} 